import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import type { RefresherEventDetail } from '@ionic/core';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import type {
  PastLeagueMemberLineupQuery,
  PastLeagueMemberLineupQueryVariables,
} from '../../../types/graphql-generated';
import { PAST_LEAGUE_MEMBER_LINEUP } from '../../../graphql/queries/pastLeagueMemberLineup';
import { EMPTY_SLOT_PENALTY_POINTS } from '../../../appConfig';
import { NegativeBudgetWarning } from './components/NegativeBudgetWarning';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LineupPitch } from '../components/lineup-editor/LineupPitch';
import { UserAvatar } from '../../components/UserAvatar';
import type { LineupSlot } from '../services/LineupService';

interface Props {
  leagueId: string;
}

const DEFAULT_TACTICS = '1-4-4-2';

type LineupPlayer = NonNullable<PastLeagueMemberLineupQuery['pastLeagueMemberLineup']>['lineupPlayers'][number];

function buildLineupSlotsFromLineupPlayers(lineupPlayers: LineupPlayer[]): LineupSlot[] {
  const byPosition = new Map(lineupPlayers.map((lp) => [lp.startingPosition, lp]));
  return Array.from({ length: 11 }, (_, i) => {
    const pos = i + 1;
    const lp = byPosition.get(pos);
    if (!lp) return { position: pos, fantasyTeamPlayerId: null, fantasyTeamPlayer: null };
    return {
      position: pos,
      fantasyTeamPlayerId: lp.id,
      fantasyScore: lp.fantasyScore,
      fantasyTeamPlayer: {
        __typename: 'FantasyTeamPlayer' as const,
        id: lp.id,
        createdAt: lp.createdAt,
        startingPosition: lp.startingPosition,
        releaseClause: null,
        signedAt: null,
        individuallyProtected: false,
        weeklyWindowProtected: false,
        releaseClausePayable: false,
        player: lp.player,
      },
    };
  });
}

export const PastLeagueMemberLineupPage = ({ leagueId }: Props) => {
  const { matchWeekId, fantasyLeagueMemberId } = useParams<
    'matchWeekId' | 'fantasyLeagueMemberId'
  >();
  const requiredMatchWeekId = matchWeekId!;
  const requiredFantasyLeagueMemberId = fantasyLeagueMemberId!;

  const { data, loading, error, refetch } = useQuery<
    PastLeagueMemberLineupQuery,
    PastLeagueMemberLineupQueryVariables
  >(PAST_LEAGUE_MEMBER_LINEUP, {
    variables: {
      matchWeekId: requiredMatchWeekId,
      fantasyLeagueMemberId: requiredFantasyLeagueMemberId,
    },
  });

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      await refetch();
    } finally {
      event.detail.complete();
    }
  };

  const lineup = data?.pastLeagueMemberLineup;
  const weekNumber = lineup?.matchWeek.weekNumber;
  const title = weekNumber != null ? `Puntos - Jornada ${weekNumber}` : 'Puntos';

  const tactics = lineup?.tactics ?? DEFAULT_TACTICS;
  const hasNegativeBudget =
    lineup?.budget !== null && lineup?.budget !== undefined && lineup.budget < 0;

  const filledCount = lineup?.lineupPlayers.length ?? 0;
  const emptyCount = 11 - filledCount;
  const calculatedPlayerScores =
    lineup?.lineupPlayers
      .map((lineupPlayer) => lineupPlayer.fantasyScore)
      .filter((score): score is number => typeof score === 'number') ?? [];
  const provisionalScore = calculatedPlayerScores.length > 0
    ? calculatedPlayerScores.reduce((total, score) => total + score, 0)
    : null;
  const showProvisionalScore = lineup?.totalScore == null && provisionalScore != null;

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref={`/app/ligas/${leagueId}/puntos/${requiredMatchWeekId}`}
            />
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {loading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar la alineación</p>
          </IonText>
        )}

        {!loading && !error && !lineup && (
          <IonText color="medium">
            <p>No hay datos</p>
          </IonText>
        )}

        {!loading && !error && lineup && (
          <>
            <div
              className="mb-3 rounded-2xl p-4 text-white shadow-[0_8px_22px_rgba(12,32,80,0.18)]"
              style={{ background: 'linear-gradient(135deg, #0a2a5b, #1a3d70)' }}
            >
              <div className="flex items-center gap-3">
                <UserAvatar
                  picture={lineup.fantasyLeagueMember.user?.picture}
                  updatedAt={lineup.fantasyLeagueMember.user?.updatedAt}
                  name={lineup.fantasyLeagueMember.teamName || lineup.fantasyLeagueMember.user?.fullName}
                  size={48}
                  className="ring-2 ring-white/70"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-heading" style={{ fontSize: '18px', fontWeight: 800, lineHeight: 1.15 }}>
                    {lineup.fantasyLeagueMember.teamName || 'Usuario desconocido'}
                  </div>
                  {lineup.fantasyLeagueMember.user?.fullName &&
                    lineup.fantasyLeagueMember.user.fullName !== lineup.fantasyLeagueMember.teamName && (
                      <div className="truncate text-[12.5px]" style={{ color: '#9fb2d4' }}>
                        {lineup.fantasyLeagueMember.user.fullName}
                      </div>
                    )}
                </div>
              </div>

              <div className="mt-3 flex items-stretch gap-3">
                <div className="flex-1">
                  <div className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: '#9fb2d4' }}>
                    Puntos totales
                  </div>
                  <div className="font-heading" style={{ fontSize: '20px', fontWeight: 800 }}>
                    {typeof lineup.totalScore === 'number' ? (
                      lineup.totalScore.toFixed(2)
                    ) : showProvisionalScore ? (
                      <span
                        className="inline-block animate-pulse motion-reduce:animate-none"
                        aria-label="Puntuación provisional"
                        title="Puntuación provisional"
                      >
                        {provisionalScore.toFixed(2)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
                <div className="w-px self-stretch" style={{ background: 'rgba(255,255,255,0.15)' }} />
                <div className="flex-1">
                  <div className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: '#9fb2d4' }}>
                    Táctica
                  </div>
                  <div className="font-heading" style={{ fontSize: '20px', fontWeight: 800 }}>
                    {tactics}
                  </div>
                </div>
              </div>
            </div>

            {hasNegativeBudget && (
              <div className="mb-3">
                <NegativeBudgetWarning />
              </div>
            )}

            {!hasNegativeBudget && emptyCount > 0 && (
              <p data-testid="lineup-penalty-summary" className="mb-3 text-sm text-red-600">
                Penalización: -{emptyCount * EMPTY_SLOT_PENALTY_POINTS} puntos por {emptyCount}{' '}
                {emptyCount === 1 ? 'posición vacía' : 'posiciones vacías'}
              </p>
            )}

            <LineupPitch
              lineup={buildLineupSlotsFromLineupPlayers(lineup.lineupPlayers)}
              tactics={tactics}
              mode="view"
            />
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
